import React, { useMemo, useState } from "react";
import "./CampaignSetup.css";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../Logic/CampaignContext";
import TimelineDisplay from "./TimelineDisplay";

const CAMPAIGN_TYPES = [
  "People of the Lantern",
  "People of the Stars",
  "People of the Sun",
  "People of the Dream Keeper",
  "Custom Campaign",
];

const PILLAR_NONE = "None";
const PILLAR_OPTIONS = [
  "Arc Survivors",
  "Encounters",
  "Scouts",
  "Seed Patterns",
  "Wanderers",
  "Characters",
  PILLAR_NONE,
];

const CUSTOM_MONSTER_OPTIONS = [
  "White Lion",
  "Screaming Antelope",
  "Phoenix",
  "Butcher",
  "King's Man",
  "The Hand",
  "Watcher",
];

// keep your presets here (you said you modified Dream Keeper)
const PREDEFINED_CAMPAIGN_MONSTERS = {
  "People of the Lantern": ["White Lion", "Screaming Antelope", "Phoenix", "Butcher", "King's Man", "The Hand", "Watcher"],
  "People of the Stars": ["White Lion", "Screaming Antelope", "Phoenix", "Butcher", "King's Man", "The Hand", "Watcher"],
  "People of the Sun": ["White Lion", "Screaming Antelope", "Phoenix", "Butcher", "King's Man", "The Hand", "Watcher"],
  "People of the Dream Keeper": ["Crimson Crocodile", "Smog Singer", "Phoenix", "The King","Butcher", "Atnas", "The Hand", "The Gambler", "Godhand"],
};

export default function CampaignSetup() {
  const nav = useNavigate();
  const {
    createNewCampaign,
    updateCampaign,
    saveCampaign,
    beginDraft,
    commitDraft,
  } = useCampaign();

  const [step, setStep] = useState("campaign");
  const [settlementName, setSettlementName] = useState("");
  const [campaignType, setCampaignType] = useState(CAMPAIGN_TYPES[0]);

  const isCustom = campaignType === "Custom Campaign";
  const isDreamKeeper = campaignType === "People of the Dream Keeper";

  const [selectedMonsters, setSelectedMonsters] = useState([]);
  const [selectedPillars, setSelectedPillars] = useState([PILLAR_NONE]);

  const pillarsAreNone = useMemo(
    () => selectedPillars.length === 1 && selectedPillars[0] === PILLAR_NONE,
    [selectedPillars]
  );

  const monstersChosen = useMemo(
    () => Array.isArray(selectedMonsters) && selectedMonsters.length > 0,
    [selectedMonsters]
  );

  const pillarsChosen = useMemo(
    () => Array.isArray(selectedPillars) && selectedPillars.length > 0,
    [selectedPillars]
  );

  const showTimeline = useMemo(() => {
    if (step !== "pillars") return false;
    if (!monstersChosen) return false;
    if (!pillarsChosen) return false;
    return true;
  }, [step, monstersChosen, pillarsChosen]);

  const toggleMonster = (name) => {
    setSelectedMonsters((prev) => (prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]));
  };

  const togglePillar = (name) => {
    setSelectedPillars((prev) => {
      if (name === PILLAR_NONE) return [PILLAR_NONE];
      const withoutNone = prev.filter((p) => p !== PILLAR_NONE);
      const has = withoutNone.includes(name);
      if (has) {
        const next = withoutNone.filter((p) => p !== name);
        return next.length ? next : [PILLAR_NONE];
      }
      return [...withoutNone, name];
    });
  };

  const startCampaign = () => {
    const name = settlementName.trim();
    if (!name) {
      alert("Settlement name is required.");
      return;
    }

    // IMPORTANT: Draft mode prevents autosave until Finish
    beginDraft();

    createNewCampaign({ settlementName: name, campaignType });

    if (isCustom) {
      updateCampaign({ settlementName: name, campaignType, monsters: [], pillars: selectedPillars });
      setStep("monsters");
      return;
    }

    const preset = PREDEFINED_CAMPAIGN_MONSTERS[campaignType] || [];
    setSelectedMonsters(preset);
    updateCampaign({ settlementName: name, campaignType, monsters: preset, pillars: selectedPillars });

    if (isDreamKeeper) {
      const allPillars = PILLAR_OPTIONS.filter((p) => p !== PILLAR_NONE);
      setSelectedPillars(allPillars);
      updateCampaign({ pillars: allPillars });
    }

    setStep("pillars");
  };

  const nextFromMonsters = () => {
    updateCampaign({ monsters: selectedMonsters });

    if (isDreamKeeper) {
      const allPillars = PILLAR_OPTIONS.filter((p) => p !== PILLAR_NONE);
      setSelectedPillars(allPillars);
      updateCampaign({ pillars: allPillars });
    }

    setStep("pillars");
  };

  const finish = async () => {
    // Write final setup into campaign state
    updateCampaign({
      settlementName: settlementName.trim(),
      campaignType,
      monsters: selectedMonsters,
      pillars: selectedPillars,
    });

    // Turn autosave back on and do ONE explicit save now
    commitDraft();
    await saveCampaign();

    nav("/dashboard");
  };

  return (
    <div className="campaign-setup-container" style={{ padding: 16 }}>
      <h2>Campaign Setup</h2>

      {step === "campaign" ? (
        <div style={{ maxWidth: 720 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 700 }}>Settlement Name</label>
            <input
              value={settlementName}
              onChange={(e) => setSettlementName(e.target.value)}
              style={{ width: "100%", padding: 8, marginTop: 6 }}
              placeholder="e.g. Tester"
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 700 }}>Campaign</label>
            <select
              value={campaignType}
              onChange={(e) => setCampaignType(e.target.value)}
              style={{ width: "100%", padding: 8, marginTop: 6 }}
            >
              {CAMPAIGN_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button onClick={startCampaign}>Next</button>
            <button onClick={() => nav("/")}>Back</button>
          </div>
        </div>
      ) : null}

      {step === "monsters" ? (
        <div style={{ maxWidth: 980 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0 }}>Select Monsters</h3>
            <div style={{ marginLeft: "auto", opacity: 0.8 }}>
              Selected: {selectedMonsters.length}
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10 }}>
            {CUSTOM_MONSTER_OPTIONS.map((m) => {
              const checked = selectedMonsters.includes(m);
              return (
                <label
                  key={m}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px 10px",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    background: checked ? "#f7f7f7" : "#fff",
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleMonster(m)} />
                  <b>{m}</b>
                </label>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={() => setStep("campaign")}>Back</button>
            <button onClick={nextFromMonsters} disabled={!monstersChosen}>
              Next
            </button>
          </div>
        </div>
      ) : null}

      {step === "pillars" ? (
        <div style={{ maxWidth: 980 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0 }}>Select Pillars</h3>
            <div style={{ marginLeft: "auto", opacity: 0.8 }}>
              {pillarsAreNone ? "None selected" : `Selected: ${selectedPillars.length}`}
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10 }}>
            {PILLAR_OPTIONS.map((p) => {
              const checked = selectedPillars.includes(p);
              return (
                <label
                  key={p}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px 10px",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    background: checked ? "#f7f7f7" : "#fff",
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => togglePillar(p)} />
                  {p}
                </label>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={() => (isCustom ? setStep("monsters") : setStep("campaign"))}>Back</button>
            <button onClick={finish} disabled={!pillarsChosen || !monstersChosen}>
              Finish
            </button>
          </div>

          {showTimeline ? (
            <div style={{ marginTop: 18 }}>
              <TimelineDisplay
                selectedCampaign={campaignType}
                selectedMonsters={selectedMonsters}
                selectedPillars={selectedPillars}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}